<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { contactRoles, contactMode } from '$lib/stores/selection';
  import { writable } from 'svelte/store';
  import { roleOptions, getRoleValue } from '$lib/utils/roleMapping';

  // Initialize mode based on contact mode from store
  const initialMode = $contactMode === 'multiple' ? 'multi' : 'single_with_fallback';
  const mode = writable(initialMode);
  const priorityRoles = writable<string[]>(['']);
  const multiRoles = writable<string[]>([]);

  // Track if user has manually changed the mode
  let userChangedMode = false;

  // Initialize from store if there are existing values
  let initialized = false;
  $: if (!initialized && $contactRoles.length > 0) {
    if ($contactMode === 'multiple') {
      multiRoles.set($contactRoles);
    } else {
      priorityRoles.set([...$contactRoles, '']);
    }
    initialized = true;
  }

  const valueFromLabel = getRoleValue;

  function updateRole(index: number, value: string) {
    priorityRoles.update((roles) => {
      const updated = [...roles];
      updated[index] = value;

      if (!value) {
        return updated.slice(0, index + 1);
      }

      if (index === updated.length - 1 && value && updated.length < 5) {
        updated.push('');
      }

      const lastFilled = updated.findLastIndex((val) => !!val);
      return updated.slice(0, lastFilled + 1).concat('');
    });
  }

  function deleteRole(index: number) {
    priorityRoles.update((roles) => {
      const newRoles = [...roles.slice(0, index)];
      // Ensure at least one empty option remains
      if (newRoles.length === 0 || newRoles[newRoles.length - 1] !== '') {
        newRoles.push('');
      }
      return newRoles;
    });
  }

  // Update store when priority roles change
  $: if ($mode === 'single_with_fallback') {
    const validRoles = $priorityRoles.filter(Boolean).map(valueFromLabel);
    contactRoles.set(validRoles);
    // Only update contact mode if user explicitly changed it
    if (userChangedMode && $contactMode !== 'single') {
      contactMode.set('single');
    }
  }

  // Handle mode changes
  $: if ($mode === 'multi') {
    contactRoles.set($multiRoles);
    // Only update contact mode if user explicitly changed it
    if (userChangedMode && $contactMode !== 'multiple') {
      contactMode.set('multiple');
    }
  }
</script>

<div class="p-3">
  <!-- Header -->
  <div class="mb-3 flex items-center">
    <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
    <span class="text-xs font-semibold text-gray-900">{$_('filters.contact.roles')}</span>
  </div>

  <!-- Mode toggle as pill switcher -->
  <div class="mb-3">
    <label class="mb-1.5 block text-xs text-gray-500">{$_('filters.contact.preference')}</label>
    <!--
      The track is `--border-default`, not gray-100. A segmented control needs
      its track to sit behind the selected pill — that is the whole device —
      and gray-100 is #f3f4f6 against a rail of #f4f4f6, so there was no track:
      a white pill floating beside grey text, with nothing to say the two were
      one control. Same triple the disabled search button uses, for the same
      reason.
    -->
    <div class="inline-flex rounded-control bg-hairline p-0.5">
      <button
        type="button"
        on:click={() => {
          mode.set('single_with_fallback');
          userChangedMode = true;
        }}
        class="rounded-control px-2.5 py-1 text-xs font-medium transition-colors duration-200 {$mode ===
        'single_with_fallback'
          ? 'bg-white text-gray-900 '
          : 'text-gray-500 hover:text-gray-700'}"
      >
        {$_('filters.contact.onePerRole')}
      </button>
      <button
        type="button"
        on:click={() => {
          mode.set('multi');
          userChangedMode = true;
        }}
        class="rounded-control px-2.5 py-1 text-xs font-medium transition-colors duration-200 {$mode ===
        'multi'
          ? 'bg-white text-gray-900 '
          : 'text-gray-500 hover:text-gray-700'}"
      >
        {$_('filters.contact.allPerRole')}
      </button>
    </div>
  </div>

  {#if $mode === 'single_with_fallback'}
    <div class="space-y-2">
      {#each $priorityRoles as _role, index (index)}
        <div>
          {#if index === 0}
            <label class="mb-1 block text-xs font-medium text-gray-700"
              >{$_('filters.contact.preferredRole')}</label
            >
          {:else}
            <label class="mb-1 block text-xs text-gray-500"
              >{$_('roles.ifNotAvailable', {
                values: {
                  role: $priorityRoles[index - 1]
                    ? $_(`roles.${valueFromLabel($priorityRoles[index - 1])}`)
                    : $_('filters.contact.selectRole').toLowerCase()
                }
              })}</label
            >
          {/if}

          <div class="flex gap-1.5">
            <select
              class="flex-1 rounded-control border border-gray-300 bg-white px-2 py-1.5 text-xs transition-colors focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              on:change={(e) => updateRole(index, (e.currentTarget as HTMLSelectElement).value)}
              bind:value={$priorityRoles[index]}
            >
              <option value="" disabled>{$_('filters.contact.selectRole')}</option>
              {#each roleOptions.filter((value) => !$priorityRoles.includes(value) || value === $priorityRoles[index]) as value (value)}
                <option {value}>{$_(`roles.${value}`)}</option>
              {/each}
            </select>
            {#if $priorityRoles.length > 1}
              <button
                type="button"
                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-control border border-gray-200 text-gray-400 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-500"
                on:click={() => deleteRole(index)}
                aria-label={$_('a11y.removeRole')}
              >
                <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if $mode === 'multi'}
    <div>
      <div class="mb-2 flex items-center justify-between">
        <label class="block text-xs font-medium text-gray-700"
          >{$_('filters.contact.selectRoles')}</label
        >
        <span class="rounded-full bg-accent-100 px-1.5 py-0.5 text-2xs font-medium text-accent-700"
          >{$multiRoles.length} / {roleOptions.length}</span
        >
      </div>

      <div class="space-y-1">
        {#each roleOptions as value (value)}
          <label
            class="group relative flex cursor-pointer items-center rounded-control border px-2 py-1.5 transition-colors duration-200 {$multiRoles.includes(
              value
            )
              ? 'border-accent-400 bg-accent-50'
              : 'border-gray-200 bg-white hover:border-accent-300 hover:bg-accent-50/50'}"
          >
            <input
              type="checkbox"
              class="peer sr-only"
              {value}
              checked={$multiRoles.includes(value)}
              on:change={(e) => {
                const checked = (e.currentTarget as HTMLInputElement).checked;
                multiRoles.update((current) =>
                  checked ? [...current, value] : current.filter((r) => r !== value)
                );
                if ($mode === 'multi') {
                  contactRoles.set($multiRoles);
                }
              }}
            />

            <!-- Custom checkbox -->
            <div
              class="relative mr-2 flex h-3.5 w-3.5 items-center justify-center rounded border-2 transition-colors duration-200 {$multiRoles.includes(
                value
              )
                ? 'border-accent-500 bg-accent-500'
                : 'border-gray-300 bg-white group-hover:border-accent-400'}"
            >
              {#if $multiRoles.includes(value)}
                <svg
                  class="h-2.5 w-2.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              {/if}
            </div>

            <!-- Label -->
            <span
              class="flex-1 text-xs {$multiRoles.includes(value)
                ? 'font-medium text-accent-900'
                : 'text-gray-700 group-hover:text-gray-900'}">{$_(`roles.${value}`)}</span
            >
          </label>
        {/each}
      </div>
    </div>
  {/if}
</div>
